import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
} from "react";
import { message } from "../../lib/notify";

const FormCtx = createContext(null);

function createFormStub() {
  return {
    setFieldsValue() {},
    getFieldsValue() {
      return {};
    },
    getFieldValue() {
      return undefined;
    },
    resetFields() {},
    submit() {},
    validateFields() {
      return Promise.resolve({});
    },
  };
}

function validateRule(value, rule) {
  if (rule.required) {
    const empty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (empty) return rule.message || "Required";
  }
  if (
    rule.type === "email" &&
    value !== undefined &&
    value !== null &&
    value !== ""
  ) {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value));
    if (!ok) return rule.message || "Invalid email";
  }
  if (rule.min != null && value != null && String(value).length < rule.min) {
    return rule.message || `Min length ${rule.min}`;
  }
  if (rule.whitespace && typeof value === "string" && !value.trim()) {
    return rule.message || "Required";
  }
  return null;
}

export function Form({
  children,
  form: formProp,
  layout = "horizontal",
  onFinish,
  onFinishFailed,
  className,
  initialValues,
}) {
  const [values, setValues] = useState(() => ({ ...initialValues }));
  const rulesRef = useRef({});

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setValues((v) => ({ ...initialValues, ...v }));
    }
  }, [initialValues]);

  const registerRules = useCallback((name, rules) => {
    if (!name) return;
    rulesRef.current[name] = rules || [];
  }, []);

  const setFieldsValue = useCallback((patch) => {
    setValues((v) => ({ ...v, ...patch }));
  }, []);

  const resetFields = useCallback(() => {
    setValues({ ...(initialValues || {}) });
  }, [initialValues]);

  const getFieldsValue = useCallback(() => values, [values]);
  const getFieldValue = useCallback((name) => values[name], [values]);

  const validateFields = useCallback(() => {
    const names = Object.keys(rulesRef.current);
    const errorFields = [];
    for (const name of names) {
      const rules = rulesRef.current[name];
      const value = values[name];
      for (const rule of rules || []) {
        const err = validateRule(value, rule);
        if (err) {
          errorFields.push({ name: [name], errors: [err] });
          message.error(err);
          break;
        }
      }
    }
    if (errorFields.length) {
      const err = { errorFields, outOfDate: false };
      return Promise.reject(err);
    }
    return Promise.resolve({ ...values });
  }, [values]);

  const submit = useCallback(
    (e) => {
      e?.preventDefault?.();
      validateFields().then(onFinish).catch(onFinishFailed ?? (() => {}));
    },
    [validateFields, onFinish, onFinishFailed]
  );

  useLayoutEffect(() => {
    if (!formProp) return;
    formProp.setFieldsValue = setFieldsValue;
    formProp.getFieldsValue = getFieldsValue;
    formProp.getFieldValue = getFieldValue;
    formProp.resetFields = resetFields;
    formProp.submit = () =>
      validateFields().then(onFinish || (() => {})).catch(onFinishFailed || (() => {}));
    formProp.validateFields = validateFields;
  }, [
    formProp,
    setFieldsValue,
    getFieldsValue,
    getFieldValue,
    resetFields,
    validateFields,
    onFinish,
    onFinishFailed,
  ]);

  const ctx = useMemo(
    () => ({
      layout,
      values,
      setFieldsValue,
      registerRules,
    }),
    [layout, values, setFieldsValue, registerRules]
  );

  return (
    <FormCtx.Provider value={ctx}>
      <form className={className} onSubmit={submit}>
        {children}
      </form>
    </FormCtx.Provider>
  );
}

export function FormItem({
  label,
  name,
  rules,
  children,
  valuePropName = "value",
  getValueFromEvent,
  normalize,
  colon = true,
  className,
  style,
  tooltip,
}) {
  const ctx = useContext(FormCtx);

  useEffect(() => {
    ctx?.registerRules(name, rules);
  }, [ctx, name, rules]);

  if (!ctx || name == null) {
    return (
      <div className={className} style={style}>
        {label != null ? (
          <span className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </span>
        ) : null}
        {children}
      </div>
    );
  }

  const value =
    ctx.values[name] ??
    (valuePropName === "checked" ? false : valuePropName === "fileList" ? [] : "");

  function onInternalChange(first) {
    let next;
    if (getValueFromEvent) next = getValueFromEvent(first);
    else if (
      first &&
      typeof first === "object" &&
      "target" in first &&
      first.target
    ) {
      const t = first.target;
      if (t.type === "checkbox") next = t.checked;
      else next = t.value;
    } else next = first;
    if (normalize) next = normalize(next);
    ctx.setFieldsValue({ [name]: next });
  }

  let childEl = React.Children.only(children);
  try {
    const origChange = childEl.props.onChange;
    childEl = React.cloneElement(childEl, {
      [valuePropName]: value,
      onChange(first, ...rest) {
        const ev = arguments.length >= 1 ? first : undefined;
        onInternalChange(ev);
        if (origChange) origChange(first, ...rest);
      },
    });
  } catch {
    /* pass through */
  }

  const isVertical = ctx.layout === "vertical";
  const rowClass = isVertical
    ? "mb-5 flex w-full flex-col gap-1.5"
    : "mb-4 items-start md:grid md:grid-cols-[minmax(0,140px)_minmax(0,1fr)] md:gap-4";

  const labelClass = isVertical
    ? "text-left text-sm font-medium leading-snug text-slate-800"
    : "pt-2 text-sm font-medium text-slate-700 md:pt-2.5 md:text-right";

  const controlClass = "w-full min-w-0";

  return (
    <div className={`${rowClass} ${className || ""}`} style={style}>
      {label != null ? (
        <label className={labelClass}>
          {rules?.some((r) => r.required) ? (
            <span className="mr-0.5 text-red-500">*</span>
          ) : null}
          {label}
          {colon && !isVertical ? ":" : ""}
          {tooltip ? (
            <span className="ml-1 text-slate-400" title={String(tooltip)}>
              (?)
            </span>
          ) : null}
        </label>
      ) : null}
      <div className={controlClass}>{childEl}</div>
    </div>
  );
}

Form.Item = FormItem;
Form.useForm = function useForm() {
  const ref = useRef(createFormStub());
  return [ref.current];
};
